import React from 'react';
import isEmpty from 'lodash/isEmpty';
import CustomTextarea from 'components/common/CustomTextarea/CustomTextarea';
import DropdownNodeButtons from 'components/ConversationRating/DropdownNodeButtons/DropdownNodeButtons';
import { StyledDropdownNode } from '../ConversationRating.styles';
import { FEEDBACK_LABEL, FOLLOW_UP_LABEL } from 'constants/ratingOptions';
import isArray from 'lodash/isArray';
import omit from 'lodash/omit';
import { SelectHolder } from './DropdownNode.styles';
import BaseSelect from 'components/common/BaseSelect/BaseSelect';

const DropdownNode = ({
  nodeTitle,
  template,
  ratingScoreDetails,
  nodesOrder,
  selectedExpert,
  expertsList = [],
  setRatingScoreDetails,
  setSelectedExpert,
  setCurrentlyActiveNode,
}) => {
  const { customInput, multipleChoises, options } = template;
  const { currentlyActiveNode, totalNodesCount } = nodesOrder;
  const selectedOptions = ratingScoreDetails[nodeTitle] || [];
  const isSelectedOptions = isArray(selectedOptions) && !isEmpty(selectedOptions);

  // if Nudge needed checked:
  //  - if there's only 1 expert on the ticket, autoselect the expert
  //  - if multiple, show select dropdown
  const isFeedbackNeeded = (details) => {
    return details[FOLLOW_UP_LABEL] && details[FOLLOW_UP_LABEL].includes(FEEDBACK_LABEL);
  };

  const isExpertSelectDropdownVisible =
    isFeedbackNeeded(ratingScoreDetails) && nodeTitle === FOLLOW_UP_LABEL && expertsList.length > 1;

  const handleSelection = (value) => {
    const clonedRatingScoreDetails = cloneRatingScoreDetailsWithNewValue(value);
    setRatingScoreDetails(clonedRatingScoreDetails);

    // if only 1 expert worked on the ticket and "Share feedback" is selected
    // the expert gets auto selected
    if (isFeedbackNeeded(clonedRatingScoreDetails) && isArray(expertsList) && expertsList.length === 1) {
      setSelectedExpert(expertsList[0] || {});
    }
  };

  const cloneRatingScoreDetailsWithNewValue = (value) => {
    // handles checkboxes (multiple selections)
    if (multipleChoises) {
      return cloneDetailsWithMultipleValue(value);
    }

    // handles checkbox selection as radio button (single selection)
    return cloneDetailsWithSingleValue(value);
  };

  const cloneDetailsWithMultipleValue = (value) => {
    if (isOptionAlreadyChecked(value)) {
      // removes item if it's already there (toggle checkbox)
      return {
        ...ratingScoreDetails,
        [nodeTitle]: selectedOptions.filter((existingValue) => existingValue !== value),
      };
    }

    // add item to state
    return {
      ...ratingScoreDetails,
      [nodeTitle]: [...selectedOptions, value],
    };
  };

  const cloneDetailsWithSingleValue = (value) => {
    if (isOptionAlreadyChecked(value)) {
      // remove selection on repeated click
      return omit(ratingScoreDetails, [nodeTitle]);
    }

    // add single property to state
    return {
      ...ratingScoreDetails,
      [nodeTitle]: [value],
    };
  };

  const isOptionAlreadyChecked = (value) => {
    return isSelectedOptions && selectedOptions.includes(value);
  };

  const handleTyping = (value) => {
    setRatingScoreDetails({
      ...ratingScoreDetails,
      [nodeTitle]: [value],
    });
  };

  // Expects text to be defined
  const emptyInput = (nodeOptions) => {
    if (isArray(nodeOptions) && nodeOptions.length === 1) {
      const isItemEmptyString = nodeOptions[0] === '' || String(nodeOptions[0]).trim() === '';

      return nodeOptions && isItemEmptyString;
    }

    return true;
  };

  const saveButtonDisabled =
    template.required && (!isSelectedOptions || (isSelectedOptions && emptyInput(selectedOptions)));

  const nextButtonDisabled =
    (template.required && !isSelectedOptions) || (isExpertSelectDropdownVisible && isEmpty(selectedExpert));

  return (
    <StyledDropdownNode className="dropDownNode">
      <h3>{nodeTitle}</h3>
      {!customInput && options && (
        <ul>
          {options.map((option) => (
            <li key={option} className="line">
              <input
                id={option}
                onChange={() => handleSelection(option)}
                type="checkbox"
                name={nodeTitle}
                checked={selectedOptions.includes(option)}
              />
              <label htmlFor={option}>{option}</label>
            </li>
          ))}
        </ul>
      )}

      {isExpertSelectDropdownVisible && (
        <>
          <h3>Select expert</h3>
          <SelectHolder>
            <BaseSelect
              defaultSelect={false}
              isClearable={true}
              value={!isEmpty(selectedExpert) ? selectedExpert : ''}
              placeholder="Select expert"
              options={expertsList}
              onChange={(option) => setSelectedExpert(option || {})}
            />
          </SelectHolder>
        </>
      )}

      {!options && customInput && (
        <CustomTextarea
          placeholder="Please add details"
          onValueChange={handleTyping}
          storedValue={selectedOptions[0] || ''}
        />
      )}

      <DropdownNodeButtons
        currentlyActiveNode={currentlyActiveNode}
        totalNodesCount={totalNodesCount}
        nextButtonDisabled={nextButtonDisabled}
        saveButtonDisabled={saveButtonDisabled}
        setCurrentlyActiveNode={setCurrentlyActiveNode}
      />
    </StyledDropdownNode>
  );
};

export default DropdownNode;
